import {
  Badge,
  Button,
  cx,
  EmptyState,
  FilterPill,
  FilterPills,
  IconTile,
  Kbd,
  ListFrame,
  ListToolbar,
  PageHeader,
  Pagination,
  RowActions,
  RowTitle,
  SearchInput,
  Select,
  TD,
  TH,
  TR,
  Table,
  SkeletonTable,
} from '@ims/ui'
import { Boxes, Hand, Package, PackagePlus, ScanSearch } from '@ims/ui/icons'
import { useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { daysUntil, expiryStatus } from '@ims/core'
import type { ProductRow, RowOf } from '@ims/data'
import { useLookups } from '../components.tsx'
import { useFeature, useFieldGroups, useLive, useSettings, useUrlAction } from '../hooks.ts'
import { RequestMoreDialog } from './purchase-requests.tsx'
import { FindSerialDialog } from './serials.tsx'
import { ReceiveStockDialog } from './receive.tsx'
import { money, qty, todayIso } from '../lib/format.ts'
import { useCan } from '../workspace.tsx'
import { SHOP_ROW_ACTIVE, StockBadge } from './products.tsx'
import { useListKeys } from '../lib/list-keys.ts'
import { moveClick, useOpen } from '../lib/motion.ts'
import { usePaged } from '../lib/paging.ts'
import {
  FIELD_HEADING,
  FieldsButton,
  columnFields,
  fieldAlign,
  fieldCell,
  phoneLine,
  useFieldData,
  useListFieldsHere,
  type FieldContext,
} from '../list-fields.tsx'

/** A product's page opened from Stock: its stock view (back to Stock). */
const stockPage = (id: string) => `/products/${id}?from=stock`

type StockShow = 'all' | 'low' | 'negative' | 'soon' | 'expired'
const SHOWS: { value: StockShow; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'low', label: 'Low' },
  { value: 'negative', label: 'Below zero' },
  { value: 'soon', label: 'Expiring soon' },
  { value: 'expired', label: 'Expired' },
]

export interface ExpiryRow {
  batch: RowOf<'batches'>
  product: ProductRow
  qtyMilli: number
  days: number
  status: 'expired' | 'soon'
}

/** Batches in stock that have expired or expire within the alert window, soonest first. */
export function useExpiringBatches(locationId: string | null = null): ExpiryRow[] | undefined {
  const settings = useSettings()
  const alertDays = settings?.expiry_alert_days ?? 30
  return useLive(
    async (repo) => {
      const today = todayIso()
      const [batches, levels] = await Promise.all([
        repo.list('batches', { limit: 100_000 }),
        repo.stock.batchLevels(locationId ? { locationId } : {}),
      ])
      const qtyByBatch = new Map<string, number>()
      for (const l of levels)
        qtyByBatch.set(l.batch_id, (qtyByBatch.get(l.batch_id) ?? 0) + l.qty_milli)
      const rows: ExpiryRow[] = []
      const products = new Map<string, ProductRow | null>()
      for (const batch of batches.rows) {
        const q = qtyByBatch.get(batch.id) ?? 0
        if (q <= 0 || !batch.expiry_date) continue
        const status = expiryStatus(batch.expiry_date, today, alertDays)
        if (status !== 'expired' && status !== 'soon') continue
        if (!products.has(batch.product_id)) {
          products.set(batch.product_id, await repo.get('products', batch.product_id))
        }
        const product = products.get(batch.product_id)
        if (!product || product.deleted_at) continue
        rows.push({
          batch,
          product,
          qtyMilli: q,
          days: daysUntil(batch.expiry_date, today),
          status,
        })
      }
      return rows.sort((a, b) => a.days - b.days)
    },
    [locationId, alertDays],
    ['batches', 'stock_movements', 'products'],
  ).data
}

export function StockScreen() {
  const lookups = useLookups()
  const openItem = useOpen()
  const canEdit = useCan('staff')
  const [search, setSearch] = useState('')
  // ?show=negative opens the list of products below zero (linked from the dashboard).
  const [params] = useSearchParams()
  const [filter, setFilter] = useState<StockShow>(() =>
    params.get('show') === 'negative' ? 'negative' : 'all',
  )
  const [locationId, setLocationId] = useState('')
  const [receivingState, setReceiving] = useState(false)
  const [askedReceive, clearAskedReceive] = useUrlAction('receive')
  const receiving = receivingState || (askedReceive && canEdit)
  const [receivingOne, setReceivingOne] = useState<ProductRow | null>(null)
  const purchaseControl = useFeature('purchase_control')
  const [requesting, setRequesting] = useState<{ product: ProductRow; qty: number } | null>(null)
  const [findingSerial, setFindingSerial] = useState(false)
  const { groups } = useFieldGroups()
  const expiring = useExpiringBatches(locationId || null)
  const data = useLive(
    async (repo) => {
      const [products, levels, onOrder, committed] = await Promise.all([
        repo.list('products', {
          where: { track_stock: true, is_active: true },
          orderBy: { column: 'name' },
          limit: 1_000_000,
        }),
        repo.stock.levels(locationId ? { locationId } : {}),
        repo.stock.onOrder(),
        repo.stock.committed(locationId ? { locationId } : {}),
      ])
      return { products: products.rows, levels, onOrder, committed }
    },
    [locationId],
    [
      'products',
      'stock_movements',
      'purchase_orders',
      'purchase_order_lines',
      'goods_receipt_lines',
      'sales_orders',
      'sales_order_lines',
      'sales_invoices',
      'sales_invoice_lines',
    ],
  ).data

  const rows = useMemo(() => {
    if (!data) return []
    const totals = new Map<string, number>()
    for (const l of data.levels)
      totals.set(l.product_id, (totals.get(l.product_id) ?? 0) + l.qty_milli)
    const term = search.trim().toLowerCase()
    return data.products
      .map((p) => ({ p, qty: totals.get(p.id) ?? 0 }))
      .filter(
        ({ p }) =>
          !term || p.name.toLowerCase().includes(term) || p.sku.toLowerCase().includes(term),
      )
      .filter(({ p, qty }) =>
        filter === 'negative' ? qty < 0 : filter === 'all' || qty <= p.min_stock_milli,
      )
  }, [data, search, filter])
  // How many products each pill holds, whatever is typed in the search box.
  const counts = useMemo(() => {
    const out: Partial<Record<StockShow, number>> = {}
    if (data) {
      const totals = new Map<string, number>()
      for (const l of data.levels)
        totals.set(l.product_id, (totals.get(l.product_id) ?? 0) + l.qty_milli)
      const of = (p: ProductRow) => totals.get(p.id) ?? 0
      out.low = data.products.filter((p) => of(p) <= p.min_stock_milli).length
      out.negative = data.products.filter((p) => of(p) < 0).length
    }
    out.soon = expiring?.filter((r) => r.status === 'soon').length
    out.expired = expiring?.filter((r) => r.status === 'expired').length
    return out
  }, [data, expiring])

  // 25 a page; a new search, filter or location starts at the first page.
  const paged = usePaged(rows, JSON.stringify([filter, search, locationId]))
  const [active, setActive] = useState<string | null>(null)
  const activeRow = paged.rows.find((r) => r.p.id === active)
  /** Low, out or below zero: the row offers Receive. */
  const receivable = (r: { p: ProductRow; qty: number }) => canEdit && r.qty <= r.p.min_stock_milli
  const listRef = useRef<HTMLDivElement>(null)
  // The fields the shop chose to show (lib/list-fields.ts); Name, In stock and Status always.
  const settings = useSettings()
  const stockFields = useListFieldsHere('stock')
  const stockColumns = columnFields(stockFields.fields)
  const fieldData = useFieldData(
    paged.rows.map((r) => r.p),
    stockFields.fields,
  )
  const fieldCtx: FieldContext = { lookups, settings, ...fieldData }
  useListKeys({
    enabled: filter !== 'soon' && filter !== 'expired',
    ids: paged.rows.map((r) => r.p.id),
    active,
    setActive,
    page: paged,
    list: listRef,
    open: () => activeRow && openItem(stockPage(activeRow.p.id)),
    keys: { r: () => activeRow && receivable(activeRow) && setReceivingOne(activeRow.p) },
  })

  return (
    <div>
      <PageHeader
        title="Stock"
        subtitle="Levels are calculated from every sale, receipt and adjustment."
        actions={
          canEdit && (
            <div className="flex flex-wrap gap-2">
              {groups.has('serials') && (
                <Button
                  variant="secondary"
                  icon={<ScanSearch className="size-4" />}
                  onClick={() => setFindingSerial(true)}
                >
                  Find serial
                </Button>
              )}
              <Button icon={<PackagePlus className="size-4" />} onClick={() => setReceiving(true)}>
                Receive stock
              </Button>
            </div>
          )
        }
      />
      <ListToolbar>
        <FilterPills label="Show">
          {SHOWS.map((f) => (
            <FilterPill
              key={f.value}
              selected={filter === f.value}
              count={f.value === 'all' ? 0 : (counts[f.value] ?? 0)}
              onSelect={() => setFilter(f.value)}
            >
              {f.label}
            </FilterPill>
          ))}
        </FilterPills>
        <div className="flex flex-wrap gap-2.5 max-sm:w-full sm:ml-auto">
          {lookups.activeLocations.length > 1 && (
            <Select
              aria-label="Location"
              className="w-44"
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
            >
              <option value="">All locations</option>
              {lookups.activeLocations.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </Select>
          )}
          <div className="flex gap-2 max-sm:w-full">
            <SearchInput
              listKeys
              className="min-w-0 max-sm:flex-1 sm:w-80"
              value={search}
              onChange={setSearch}
              placeholder="Filter products…"
            />
            <FieldsButton list="stock" api={stockFields} />
          </div>
        </div>
      </ListToolbar>
      {filter === 'soon' || filter === 'expired' ? (
        <ExpiryTable
          key={`${filter}|${search}|${locationId}`}
          rows={(expiring ?? []).filter((r) => r.status === filter)}
          loading={expiring === undefined}
          search={search}
          empty={filter === 'expired' ? 'No expired stock' : 'Nothing expires soon'}
        />
      ) : (
        <ListFrame ref={listRef}>
          {!data ? (
            <SkeletonTable />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<Boxes className="size-6" />}
              title={
                search
                  ? 'No matching products'
                  : filter === 'low'
                    ? 'Nothing is running low'
                    : filter === 'negative'
                      ? 'Nothing is below zero'
                      : 'Nothing is tracked yet'
              }
              description={
                search || filter === 'low' || filter === 'negative'
                  ? undefined
                  : 'Turn on “Track stock” for a product, or receive stock, and its level shows here.'
              }
            />
          ) : (
            <>
              <Table stack="cards">
                <thead>
                  <tr>
                    <TH>Product</TH>
                    {stockColumns.map((f) => (
                      <TH key={f} align={fieldAlign(f)}>
                        {FIELD_HEADING[f]}
                      </TH>
                    ))}
                    <TH align="right">In stock</TH>
                    <TH>Status</TH>
                    <TH />
                  </tr>
                </thead>
                <tbody>
                  {paged.rows.map((r) => {
                    const { p, qty: q } = r
                    const unit = lookups.unit(p.unit_id)?.symbol
                    const ordered = data.committed[p.id] ?? 0
                    const onOrder = data.onOrder[p.id] ?? 0
                    const fieldRow = { p, inStock: q, tracked: true }
                    return (
                      <TR
                        key={p.id}
                        onClick={() => openItem(stockPage(p.id))}
                        aria-current={active === p.id ? 'true' : undefined}
                        className={cx(active === p.id && SHOP_ROW_ACTIVE)}
                      >
                        <TD className="stack-title">
                          <RowTitle
                            tile={
                              <IconTile area="stock" size="sm">
                                <Boxes className="size-4" />
                              </IconTile>
                            }
                            title={
                              <Link
                                to={stockPage(p.id)}
                                className="hover:underline focus-visible:underline"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  moveClick(stockPage(p.id), 'open')(e)
                                }}
                              >
                                {p.name}
                              </Link>
                            }
                            sub={
                              <>
                                {/* A phone card: what is left, then the first two chosen fields. */}
                                <span className="sm:hidden">
                                  {[
                                    `${qty(q)}${unit ? ` ${unit}` : ''}`,
                                    ...phoneLine('stock', stockFields.fields, fieldRow, fieldCtx),
                                  ].join(' · ')}
                                </span>
                                <span className="max-sm:hidden">
                                  {stockFields.has('sku') ? p.sku : null}
                                </span>
                              </>
                            }
                          />
                        </TD>
                        {stockColumns.map((f) => (
                          <TD
                            key={f}
                            align={fieldAlign(f)}
                            className={cx(
                              'text-[13.5px] max-sm:hidden',
                              fieldAlign(f) === 'right' && 'tabular whitespace-nowrap text-muted',
                            )}
                          >
                            {fieldCell(f, fieldRow, fieldCtx)}
                          </TD>
                        ))}
                        <TD align="right" className="tabular max-sm:hidden">
                          <span className={q < 0 || q === 0 ? 'text-danger' : undefined}>
                            {qty(q)} {unit}
                          </span>
                          {/* Promised to customers on confirmed orders that are not billed yet. */}
                          {ordered > 0 && (
                            <div className="text-xs text-muted">
                              {qty(ordered)} ordered by customers ·{' '}
                              <span className={q - ordered < 0 ? 'text-danger' : undefined}>
                                {qty(q - ordered)} free
                              </span>
                            </div>
                          )}
                          {/* Still to arrive on purchase orders that have been sent. */}
                          {onOrder > 0 && (
                            <div className="text-xs text-muted">+{qty(onOrder)} on order</div>
                          )}
                        </TD>
                        <TD className="stack-end stack-r1">
                          {q < 0 ? (
                            <Badge tone="danger">Below zero</Badge>
                          ) : (
                            <StockBadge qtyMilli={q} minMilli={p.min_stock_milli} track />
                          )}
                        </TD>
                        <TD className="stack-actions sm:w-px sm:whitespace-nowrap">
                          <RowActions>
                            {receivable(r) && (
                              <Button
                                size="sm"
                                variant="soft"
                                icon={<Package className="size-4" />}
                                className="h-[34px] text-[13px]"
                                aria-label={`Receive ${p.name}`}
                                onClick={() => setReceivingOne(p)}
                              >
                                Receive
                              </Button>
                            )}
                            {purchaseControl && receivable(r) && (
                              <Button
                                size="sm"
                                variant="secondary"
                                icon={<Hand className="size-4" />}
                                className="h-[34px] text-[13px]"
                                aria-label={`Request ${p.name}`}
                                onClick={() => setRequesting({ product: p, qty: q })}
                              >
                                Request
                              </Button>
                            )}
                          </RowActions>
                        </TD>
                      </TR>
                    )
                  })}
                </tbody>
              </Table>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-2.5 text-xs text-muted max-sm:px-1">
                <span>A to Z. A row opens the product.</span>
                <span className="ml-auto flex items-center gap-1.5 max-lg:hidden">
                  <Kbd>↑</Kbd> <Kbd>↓</Kbd> move <Kbd>Enter</Kbd> open{' '}
                  {canEdit && (
                    <>
                      <Kbd>R</Kbd> receive
                    </>
                  )}
                </span>
              </div>
              <Pagination
                offset={paged.offset}
                limit={paged.limit}
                total={paged.total}
                onChange={paged.setOffset}
              />
            </>
          )}
        </ListFrame>
      )}
      {receiving && (
        <ReceiveStockDialog
          onClose={() => {
            setReceiving(false)
            if (askedReceive) clearAskedReceive()
          }}
        />
      )}
      {receivingOne && (
        <ReceiveStockDialog products={[receivingOne]} onClose={() => setReceivingOne(null)} />
      )}
      {requesting && (
        <RequestMoreDialog
          product={requesting.product}
          stockMilli={requesting.qty}
          onClose={() => setRequesting(null)}
        />
      )}
      {findingSerial && <FindSerialDialog onClose={() => setFindingSerial(false)} />}
    </div>
  )
}

function ExpiryTable({
  rows,
  loading,
  search,
  empty,
}: {
  rows: ExpiryRow[]
  loading: boolean
  search: string
  empty: string
}) {
  const openItem = useOpen()
  const lookups = useLookups()
  const settings = useSettings()
  const term = search.trim().toLowerCase()
  const shown = rows.filter(
    (r) =>
      !term ||
      r.product.name.toLowerCase().includes(term) ||
      r.batch.batch_no.toLowerCase().includes(term),
  )
  const paged = usePaged(shown, term)
  const [active, setActive] = useState<string | null>(null)
  const activeRow = paged.rows.find((r) => r.batch.id === active)
  const listRef = useRef<HTMLDivElement>(null)
  useListKeys({
    ids: paged.rows.map((r) => r.batch.id),
    active,
    setActive,
    page: paged,
    list: listRef,
    open: () => activeRow && openItem(`/products/${activeRow.product.id}`),
    keys: {},
  })
  if (loading)
    return (
      <ListFrame>
        <SkeletonTable />
      </ListFrame>
    )
  if (shown.length === 0)
    return (
      <ListFrame>
        <EmptyState icon={<Boxes className="size-6" />} title={empty} />
      </ListFrame>
    )
  const value = shown.reduce(
    (a, r) =>
      a + Math.round((r.qtyMilli * (r.batch.cost_paisa ?? r.product.cost_price_paisa ?? 0)) / 1000),
    0,
  )
  return (
    <ListFrame ref={listRef}>
      <Table stack="cards">
        <thead>
          <tr>
            <TH>Product</TH>
            <TH>Expiry</TH>
            <TH align="right">In stock</TH>
          </tr>
        </thead>
        <tbody>
          {paged.rows.map((r) => (
            <TR
              key={r.batch.id}
              onClick={() => openItem(`/products/${r.product.id}`)}
              aria-current={active === r.batch.id ? 'true' : undefined}
              className={cx(active === r.batch.id && SHOP_ROW_ACTIVE)}
            >
              <TD className="stack-title">
                <RowTitle
                  tile={
                    <IconTile area="stock" size="sm">
                      <Boxes className="size-4" />
                    </IconTile>
                  }
                  title={
                    <Link
                      to={`/products/${r.product.id}`}
                      className="hover:underline focus-visible:underline"
                      onClick={(e) => {
                        e.stopPropagation()
                        moveClick(`/products/${r.product.id}`, 'open')(e)
                      }}
                    >
                      {r.product.name}
                    </Link>
                  }
                  sub={`Batch ${r.batch.batch_no}`}
                />
              </TD>
              <TD className="stack-end stack-r1">
                <span className="max-sm:hidden">{r.batch.expiry_date} </span>
                <Badge tone={r.status === 'expired' ? 'danger' : 'warning'}>
                  {r.status === 'expired'
                    ? `${-r.days} day${r.days === -1 ? '' : 's'} ago`
                    : r.days === 0
                      ? 'today'
                      : `in ${r.days} day${r.days === 1 ? '' : 's'}`}
                </Badge>
              </TD>
              <TD align="right" className="tabular stack-end stack-r2">
                {qty(r.qtyMilli)} {lookups.unit(r.product.unit_id)?.symbol}
              </TD>
            </TR>
          ))}
        </tbody>
      </Table>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-2.5 text-xs text-muted max-sm:px-1">
        <span>
          {shown.length} batch{shown.length === 1 ? '' : 'es'} · worth {money(value, settings)} at
          cost
        </span>
        <span className="ml-auto flex items-center gap-1.5 max-lg:hidden">
          <Kbd>↑</Kbd> <Kbd>↓</Kbd> move <Kbd>Enter</Kbd> open
        </span>
      </div>
      <Pagination
        offset={paged.offset}
        limit={paged.limit}
        total={paged.total}
        onChange={paged.setOffset}
      />
    </ListFrame>
  )
}
